import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Coffee } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ARTICLE = `
(روایتی مستند از دانه‌ای که جهان را بیدار کرد)


مقدمه

قهوه، تنها یک نوشیدنی انرژی‌زا نیست؛ نمادی از فرهنگ، تجارت و پیوند میان ملت‌هاست. سفری که از کوهستان‌های اتیوپی آغاز شد، از یمن و شبه‌جزیره عربستان گذشت و از طریق جاده ابریشم به ایران و سپس سراسر جهان رسید.
در این مسیر طولانی، قهوه نه‌تنها اقتصاد و تجارت را دگرگون کرد، بلکه شیوه‌ی تعامل و معاشرت مردم را نیز تغییر داد.

آغاز داستان؛ کشف قهوه در اتیوپی

بر اساس روایت مشهور، حدود قرن نهم میلادی، چوپانی به نام کالدی (Kaldi) در ارتفاعات اتیوپی متوجه شد بزهایش پس از خوردن دانه‌های گیاه خاصی، سرزنده‌ تر و پرتحرک‌ تر می‌شوند. او دانه‌ها را به صومعه‌ای محلی برد و راهبان پس از جوشاندن آن، نوشیدنی تلخی به‌دست آوردند که باعث می‌شد شب‌ها بیدار بمانند و عبادت کنند.
این روایت در Smithsonian Magazine (2016) و National Coffee Association USA به‌عنوان یکی از قدیمی‌ترین افسانه‌های مستند درباره پیدایش قهوه ذکر شده است.

⸻

از یمن تا مکه؛ تولد نخستین قهوه‌خانه‌ها

قهوه از اتیوپی به یمن راه یافت، جایی که در بندر موکا (Mocha) کشت تجاری آن آغاز شد.
صوفیان یمنی قهوه را در آیین‌های شبانه‌ی خود برای تمرکز و بیداری مصرف می‌کردند. از قرن پانزدهم میلادی، نوشیدنی قهوه به‌تدریج وارد مکه، مدینه و قاهره شد و نخستین قهوه‌خانه‌های جهان در آنجا شکل گرفتند.

ورود قهوه به ایران؛ نقش جاده ابریشم

ایران، در مسیر طلایی جاده ابریشم، پلی میان شرق و غرب بود. کاروان‌هایی که از هند، یمن و عثمانی عبور می‌کردند، قهوه را نیز با خود آوردند.
در دوران صفویه (قرن ۱۶ میلادی)، قهوه از طریق بندرعباس و بوشهر وارد ایران شد و در شهرهایی چون اصفهان، شیراز و تبریز، میان بازرگانان و اشراف محبوبیت یافت.

طبق پژوهش Iranian Journal of History (Vol. 12, 2019)، نخستین قهوه‌خانه‌های ایرانی در زمان شاه‌عباس اول تأسیس شدند.

قهوه‌خانه‌های صفوی؛ نقطه‌ی اتصال مردم و اندیشه‌ها

در دوران صفوی، قهوه‌خانه‌ها فراتر از مکانی برای نوشیدن قهوه بودند؛ آنها به محفل شعر، موسیقی، سیاست و گفتگو تبدیل شدند.
در اصفهانِ قرن هفدهم، نقاشان و شاعران نامدار آثارشان را در قهوه‌خانه‌ها به نمایش می‌گذاشتند و مردم از طبقات مختلف در فضایی برابر به گفتگو می‌نشستند.
این سنت بعدها به عثمانی، بالکان و اروپا نیز سرایت کرد — جایی که بعدها به شکل «کافه»‌های پاریس و وین ادامه یافت.

تفاوت قهوه عربیکا و روبوستا؛ انتخاب کاروان‌های جاده ابریشم

در مسیرهای بازرگانی جاده ابریشم، دو گونه‌ی اصلی قهوه جابجا می‌شد:
 • عربیکا (Arabica): منشأ آن یمن و حبشه بود، طعم لطیف‌تر، اسیدیته‌ی متعادل و عطر میوه‌ای داشت.
 • روبوستا (Robusta): از نواحی آفریقای مرکزی می‌آمد، طعمی تلخ‌تر، بادی قوی‌تر و کافئینی بالاتر داشت.

کاروان‌های تجاری ایران غالباً عربیکا را برای اشراف و روبوستا را برای کاروان‌نشینان و مناطق گرم‌تر حمل می‌کردند. این تقسیم، بعدها در فرهنگ نوشیدن قهوه ایران نیز تأثیر گذاشت.


از قهوه به چای؛ دگرگونی ذائقه ایرانی

با گسترش روابط ایران و چین در اواخر دوران صفویه، چای جایگزین تدریجی قهوه شد.
اما در شهرهای جنوبی ایران مانند بوشهر، قهوه همچنان جایگاه خود را حفظ کرد.
در مجله باستان‌شناسی خلیج فارس (جلد ۵، ۲۰۱۸) آمده است که در مراسم محلی بوشهر و قشم، هنوز آیین سنتی دم‌کردن قهوه عربی رایج است.

قهوه در ایران معاصر

در قرن بیستم، با ورود برندهای اروپایی و قهوه فوری، مصرف قهوه در ایران شکل تازه‌ای به خود گرفت.
اما در دهه‌های اخیر، موج جدیدی از قهوه تخصصی (Specialty Coffee) و رُست ایرانی پدید آمده که برندهایی همچون خورشید جاده ابریشم نماینده‌ی آن هستند — ترکیبی از اصالت شرقی و دانش مدرن برشته‌کاری.

جمع‌بندی؛ از افسانه تا فنجان امروز

قهوه مسیری طولانی از دشت‌های آفریقا تا کاروان‌سراهای جاده ابریشم پیموده است.
این نوشیدنی نه‌ فقط میراثی تاریخی، بلکه فرهنگی زنده است که هنوز میان ایرانیان جایگاه خاص خود را دارد.

☀️ شما هم به دنبال طعم واقعی قهوه هستید؟
 از عربیکای اصیل تا روبوستای پرقدرت، می‌توانید از طریق وب‌سایت خورشید جاده ابریشم یا واتس‌اپ رسمی مجموعه، دانه‌ِ تازه‌ِ برشته‌ِ موردنظرتان را سفارش دهید و طعم هزار سال تاریخ را در فنجان خود تجربه کنید.
`;

export default function CoffeeHistoryComponent() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 via-amber-100 to-amber-50 p-6">
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="max-w-4xl w-full bg-white/80 backdrop-blur-md rounded-2xl shadow-2xl p-2 mt-12 border border-amber-100"
        aria-label="تاریخچه قهوه و ورود آن به ایران و جاده ابریشم"
      >
        <header className="flex justify-start items-start gap-4 mb-6">
          <motion.div
            whileTap={{ scale: 0.95, rotate: -3 }}
            className="ml-auto flex items-center gap-2 text-amber-700 active:scale-95 cursor-pointer"
            title="خورشید جاده ابریشم"
            onClick={() => navigate('/home')}
          >
            <Sun className="w-6 h-6" />
            <span className="text-l font-medium">خورشید جاده ابریشم</span>
          </motion.div>
        </header>

        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-200/60 shadow-inner">
            <Coffee className="w-5 h-5 text-amber-900" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-amber-900">تاریخچه قهوه و ورود آن به ایران و جاده ابریشم</h1>
          </div>
        </div>

        <section className="text-sm max-w-none text-amber-900 leading-7 text-justify">
          <p style={{ whiteSpace: 'pre-wrap' }}>{ARTICLE}</p>
        </section>

        <footer className="mt-8 flex flex-col items-start gap-3 justify-start">
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-2 bg-amber-900 text-white px-4 py-2 rounded-lg shadow hover:shadow-lg"
            onClick={() => navigate('/home', { state: { scrollTo: 'products' } })}
          >
            <Coffee className="w-4 h-4" />
            <span className="text-xs">سفارش دانه تازه</span>
          </motion.button>

          <div className="text-xs text-amber-700/80">طراحی: خورشید جاده ابریشم — تجربه‌ای نو از قهوه و تاریخ</div>
        </footer>

        <div className="mt-6 flex justify-end">
          <CopyArticleButton text={ARTICLE} />
        </div>
      </motion.article>
    </div>
  );
}

function CopyArticleButton({ text }) {
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {}
  };

  return (
    <motion.button
      onClick={copy}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="text-amber-700/90 bg-amber-50 border border-amber-100 px-3 py-2 rounded-md text-sm shadow-sm"
      aria-pressed={copied}
    >
      {copied ? 'کپی شد ✓' : 'کپی متن'}
    </motion.button>
  );
}
